import React, { PropsWithChildren, useContext, useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { matchPath, useLocation, useSearchParams } from 'react-router';
import { generateFetchAdQuery } from '@moment/net/ad-fetch-query.ts';
import { IAdTagResponce } from '@pages/Root/services/api/ads/ad-model-definitions.ts';
import { GamAdsInitLayout } from './GamAdsInitLayout.tsx';
import { DTXAdsLayout } from './DTXAdsLayout.tsx';
import { AdsContext } from '@moment/context/AdsContext.tsx';
import { AdCounterContext } from '@moment/context/AdCounterContext.tsx';
import { CommonPageDataContext } from '@pages/Root/ContextWrapper/WithInterest/CommonPageDataContext.ts';
import { initializeAds, loadAdScripts, loadAllAdScripts, switchPubwiseBundle } from '@pages/Root/utils/dtx-ads.ts';
import { initPrebidAnalytics } from '@moment/utils/ad-prebid-analytics.ts';
import { AdsConfigOverride, AdsConfigOverrideContext } from '@moment/context/AdsConfigOverrideContext.tsx';
import { LOOKBOOK_ROUTE_PATH } from '@pages/Root/router/constants.ts';
import { disablePubwise, enablePubwise, tearDownPubwise } from '@moment/srv/utils/ad-utils.ts';
import { isDntEnabled } from '@utils/dnt.ts';
import { useAdStackAnalytics } from '@moment/analytics/AnalyticsProvider.tsx';
import {
  logAdImpressionMetric,
  logAdNotRenderedMetric,
  logAdRenderedMetric,
  logAdRequestedMetric,
} from '@/tracing/events.ts';

interface AdsLayoutProps extends PropsWithChildren {
  brandGroupIdOverride?: string;
  utmSourceOverride?: string;
}

// Pages whose ad config is only known once their own data has loaded. On these routes the root
// holds the ad config fetch and the pubwise load until the page publishes its override, so the
// default config is never loaded first. Derived from the location (not from a child effect) so
// there is no render in which the default key looks final.
const isConfigPendingRoute = (pathname: string) => !!matchPath(LOOKBOOK_ROUTE_PATH, pathname);

/**
 * There must be exactly one AdsLayout per page: it owns the ad scripts (pubwise, DTX, GAM) that
 * live on window. A nested instance is a passthrough; pages publish their brand group / utm
 * source with useAdsConfigOverride instead of mounting their own AdsLayout.
 */
export const AdsLayout: React.FC<AdsLayoutProps> = (props) => {
  const parent = useContext(AdsContext);
  if (parent) {
    console.warn('Nested AdsLayout ignored: use useAdsConfigOverride to change the ad config');
    return <>{props.children}</>;
  }
  return <RootAdsLayout {...props} />;
};

const RootAdsLayout: React.FC<AdsLayoutProps> = ({ brandGroupIdOverride, utmSourceOverride, children }) => {
  const [dtxScriptLoaded, setDTXScriptLoaded] = useState(false)
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const [dtxInit, setDTXInit] = useState(false)
  const [override, setOverrideState] = useState<AdsConfigOverride | null>(null)

  const setOverride = useCallback((next: AdsConfigOverride | null) => {
    setOverrideState((prev) =>
      prev?.brandGroupId === next?.brandGroupId && prev?.utmSource === next?.utmSource ? prev : next
    );
  }, []);
  const overrideContextValue = useMemo(() => ({ setOverride }), [setOverride]);

  const { pathname } = useLocation();
  const pending = isConfigPendingRoute(pathname) && !override;

  const commonContext = useContext(CommonPageDataContext);
  const brandGroupId = override?.brandGroupId || brandGroupIdOverride || commonContext.brandGroupId;
  const utmSource = override?.utmSource || utmSourceOverride || commonContext.utmSource;
  const currentKey = `${brandGroupId}|${utmSource}`;
  const { appBundle, appName, appVersion, partner } = commonContext;
  const [searchParams] = useSearchParams();
  const adStackAnalytics = useAdStackAnalytics();
  const dtxInitStartedRef = useRef(false);

  const getMpid = useCallback((): string | undefined => {
    try {
      return searchParams.get('mpid') || searchParams.get('uid') || localStorage.getItem('mpid') || undefined;
    } catch (e) {
      console.log('Error reading mpid', e);
      return undefined;
    }
  }, [searchParams]);

  // Ad counter functionality for generating unique ad IDs
  const adCounter = useRef(0);
  const getAndIncrement = useCallback(() => {
    adCounter.current += 1;
    return adCounter.current;
  }, []);

  const adCounterContextValue = useMemo(
    () => ({ getAndIncrement }),
    [getAndIncrement]
  );

  const adQueryFn = useCallback(() => {
    const fetcher = generateFetchAdQuery()(brandGroupId, utmSource);
    return fetcher();
  }, [brandGroupId, utmSource]);

  const { data: adConfigData, isLoading } = useQuery<IAdTagResponce>({
    queryKey: ['adConfig', brandGroupId, utmSource],
    queryFn: adQueryFn,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    enabled: !!brandGroupId && !pending,
  });

  // The adSource is determined by the runtime configuration, which is provided by CommonPageDataContext.
  const adSource = useMemo(() => commonContext.adSource || 'none', [commonContext.adSource]);
  const hasGam = useMemo(() => adSource.includes('gam'), [adSource]);
  const hasDtx = useMemo(() => adSource.includes('dtx'), [adSource]);

  const handleAdImpression = useCallback((data: Record<string, object | string>) => {
    console.log(`DTX ad event 'impression' received: ${JSON.stringify(data)}`);
    const slotId: string = data.targetElementId as string;
    const adTagId: string = data.placementId as string;
    adStackAnalytics.emitViewableImpressionEvent({
      partnerId: partner || '',
      adTag: adTagId,
      adUnitId: slotId,
      searchParams,
    });
    logAdImpressionMetric({
      ad_unit_id: slotId,
      ad_tag: adTagId,
      utm_source: utmSource,
    });
  }, [adStackAnalytics, partner, searchParams, utmSource]);

  const handleAdRequested = useCallback((data: Record<string, object | string>) => {
    console.log(`DTX ad event 'ad_requested' received: ${JSON.stringify(data)}`);
    const slotId: string = data.targetElementId as string;
    const adTagId: string = data.placementId as string;
    logAdRequestedMetric({
      ad_unit_id: slotId,
      ad_tag: adTagId || '',
    });
  }, []);

  const handleAdRendered = useCallback((data: Record<string, object | string>) => {
    console.log(`DTX ad event 'ad_loaded' received: ${JSON.stringify(data)}`);
    const slotId: string = data.targetElementId as string;
    const adTagId: string = data.placementId as string;
    adStackAnalytics.emitImpressionEvent({
      partnerId: partner || '',
      adTag: adTagId,
      adUnitId: slotId,
      searchParams,
    });
    logAdRenderedMetric({
      ad_unit_id: slotId,
      ad_tag: adTagId || '',
    });
  }, [adStackAnalytics, partner, searchParams]);

  const handleAdNotRendered = useCallback((data: Record<string, object | string>) => {
    console.log(`DTX ad event 'ad_load_failed' received: ${JSON.stringify(data)}`);
    const slotId: string = data.targetElementId as string;
    const adTagId: string = data.placementId as string;
    adStackAnalytics.emitAdNotRenderedEvent({
      partnerId: partner || '',
      adTag: adTagId,
      adUnitId: slotId,
      searchParams,
    });
    logAdNotRenderedMetric({
      ad_unit_id: slotId,
      ad_tag: adTagId || '',
    });
  }, [adStackAnalytics, partner, searchParams]);

  // The DTX listeners are registered once, so they delegate to the latest handlers (latest utm,
  // search params and partner) instead of the ones captured on the first render.
  const dtxHandlersRef = useRef({ handleAdImpression, handleAdRequested, handleAdRendered, handleAdNotRendered });
  dtxHandlersRef.current = { handleAdImpression, handleAdRequested, handleAdRendered, handleAdNotRendered };

  useEffect(() => {
    if (hasGam && !isDntEnabled()) {
      loadAllAdScripts(['//securepubads.g.doubleclick.net/tag/js/gpt.js']).catch((e) =>
        console.error('Error loading gpt.js', e)
      );
    }
  }, [hasGam]);

  useEffect(() => {
    if ((hasGam || hasDtx) && !isDntEnabled()) {
      loadAllAdScripts(['//tagan.adlightning.com/mobileposse/op.js']).catch((e) =>
        console.error('Error loading op.js', e)
      );
    }
  }, [hasGam, hasDtx]);

  useEffect(() => {
    // Load and initialize the DTX SDK unconditionally (not gated on hasDtx) so that
    // prebid — which can invoke DTX bidding via window.DTXWebPrebidSpec regardless of
    // adSource — always has an initialized DTX SDK available in advance.
    if (dtxScriptLoaded || dtxInitStartedRef.current) {
      return;
    }
    dtxInitStartedRef.current = true;

    if (isDntEnabled()) {
      setDTXScriptLoaded(true);
      setDTXInit(true);
      return;
    }

    loadAdScripts()
      .then(() => {
        setDTXScriptLoaded(true);
        const aaid = searchParams.get('aaid');
        const mpid = getMpid();
        initializeAds(
          [
            { eventName: 'impression', callback: (d) => dtxHandlersRef.current.handleAdImpression(d) },
            { eventName: 'ad_requested', callback: (d) => dtxHandlersRef.current.handleAdRequested(d) },
            { eventName: 'ad_loaded', callback: (d) => dtxHandlersRef.current.handleAdRendered(d) },
            { eventName: 'ad_load_failed', callback: (d) => dtxHandlersRef.current.handleAdNotRendered(d) },
          ],
          appBundle,
          appName,
          appVersion,
          aaid || undefined,
          mpid
        );
        setDTXInit(true);
      })
      .catch((e) => {
        // Fail open: a blocked/failed DTX SDK must not take pubwise and GAM down with it. DTX ads
        // can't render without it, but everything else is gated on dtxInit.
        console.error('Error loading DTX SDK', e);
        setDTXScriptLoaded(true);
        setDTXInit(true);
      });
  }, [dtxScriptLoaded, appBundle, appName, appVersion, searchParams, getMpid]);

  useEffect(() => {
    // Wait for the DTX SDK to be initialized (not just script-loaded) before loading the prebid
    // wrapper — otherwise window.DTXWebPrebidSpec may be undefined when pbjs.que fires registerBidAdapter.
    // Loaded per effective {brandGroupId, utmSource}: when the key changes (client-side navigation to
    // a page with another config) the pubwise bundle is switched, but only if it is a different
    // bundle (see switchPubwiseBundle).
    if (!adConfigData || !dtxInit || pending || loadedKey === currentKey) {
      return undefined;
    }
    let cancelled = false;
    const pubwisePreScript = adConfigData.adTag?.pubwisePreScript
    const pubwiseScript = adConfigData.adTag?.pubwiseScript
    if ((pubwiseScript || pubwisePreScript) && !isDntEnabled()) {
      // Registered before the bundle loads so Prebid runs it from the que (idempotent per que).
      initPrebidAnalytics(adStackAnalytics)
      switchPubwiseBundle([pubwiseScript, pubwisePreScript], {
        isCurrent: () => !cancelled,
        teardown: () => tearDownPubwise(),
        beforeLoad: () => enablePubwise(),
      })
        .then((loaded) => {
          if (loaded && !cancelled) {
            setLoadedKey(currentKey)
          }
        })
        .catch((e) => {
          // Fail open: without pubwise, slots must fall back to plain GAM refreshes
          // (refreshAdd routes through pbjs.que while window.pubwise.enabled is true).
          console.error('Error loading pubwise scripts', e)
          if (!cancelled) {
            disablePubwise()
            setLoadedKey(currentKey)
          }
        })
    } else {
      setLoadedKey(currentKey)
    }
    return () => {
      cancelled = true
    }
  }, [adConfigData, dtxInit, pending, loadedKey, currentKey, adStackAnalytics]);


  // The ad system is ready when the config is loaded, and any required ad systems are initialized.
  const isReady = useMemo(() => {
    const configLoaded = !isLoading;
    // If DTX is not used, its readiness is not a factor.
    // If DTX is used, we need its scripts loaded and the system initialized.
    const dtxReady = !hasDtx || (dtxScriptLoaded && dtxInit);

    // GAM doesn't have a separate initialization signal in this component, so its readiness
    // is tied to the config being loaded.
    // While the page's own config is pending, the loaded key still belongs to the previous page:
    // not ready, or slots would render against the previous config's pubwise units.
    return configLoaded && dtxReady && !pending && loadedKey === currentKey;
  }, [isLoading, hasDtx, dtxScriptLoaded, dtxInit, pending, loadedKey, currentKey]);

  // Memoize the context value to prevent unnecessary re-renders in consumers.
  const contextValue = useMemo(() => ({
    isReady,
    adConfigData: adConfigData || null,
    adSource,
    configKey: currentKey,
  }), [isReady, adConfigData, adSource, currentKey]);

  const content = useMemo(() => {
    if (!hasDtx && !hasGam) {
      return children
    }
    let currentContent = children
    if (hasGam) {
      currentContent = (
        <GamAdsInitLayout brandGroupId={brandGroupId} utmSource={utmSource}>
          {currentContent}
        </GamAdsInitLayout>
      );
    }

    if (hasDtx) {
      currentContent = <DTXAdsLayout>{currentContent}</DTXAdsLayout>;
    }
    return currentContent
  }, [hasDtx, hasGam, children, brandGroupId, utmSource]);


  return (
    <AdCounterContext.Provider value={adCounterContextValue}>
      <AdsConfigOverrideContext.Provider value={overrideContextValue}>
        <AdsContext.Provider value={contextValue}>
          {content}
        </AdsContext.Provider>
      </AdsConfigOverrideContext.Provider>
    </AdCounterContext.Provider>
  );
};
